<template>
    <div
        id="main-wrapper"
        class="attendee-wrapper"
        :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
    >
        <NavbarDark />

        <main class="attendee-page">

            <div class="background-decoration decoration-one"></div>
            <div class="background-decoration decoration-two"></div>

            <div v-if="loading" class="state-container">
                <div class="loading-card">
                    <div class="loading-logo">
                        <span></span>
                    </div>

                    <div class="loading-line loading-line-large"></div>
                    <div class="loading-line"></div>
                    <div class="loading-line loading-line-small"></div>

                    <p>
                        {{ $i18n.locale === 'ar'
                            ? 'جاري تحميل بيانات الزائر...'
                            : 'Loading attendee information...' }}
                    </p>
                </div>
            </div>

            <!-- Error -->
            <div v-else-if="error" class="state-container">
                <div class="error-card">

                    <div class="error-icon">
                        <i class="fa-solid fa-user-xmark"></i>
                    </div>

                    <h2>
                        {{ $i18n.locale === 'ar'
                            ? 'لم يتم العثور على البيانات'
                            : 'Attendee Not Found' }}
                    </h2>

                    <p>
                        {{ $i18n.locale === 'ar'
                            ? 'عذراً، لا يمكن العثور على بيانات هذا الزائر.'
                            : 'Sorry, we could not find this attendee.' }}
                    </p>

                </div>
            </div>

            <!-- Attendee Card -->
            <div v-else-if="attendee" class="attendee-card">
                    <!-- Exhibit Banner -->
                    <div
                        v-if="attendee.exhibit?.banner"
                        class="banner-section"
                    >
                        <img
                            :src="attendee.exhibit.banner"
                            :alt="exhibitName"
                            class="exhibit-banner"
                        />
                    </div>

                    <div
                        v-else
                        class="logo-section"
                    >
                        <div class="logo-placeholder">
                            <i class="fa-solid fa-building"></i>
                        </div>
                    </div>

                <!-- Exhibit Information -->
                <div class="exhibit-section">

                    <div
                        v-if="attendee.exhibit?.name?.en"
                        class="exhibit-name-en"
                    >
                        {{ attendee.exhibit.name.en }}
                    </div>

                    <div
                        v-if="attendee.exhibit?.name?.ar"
                        class="exhibit-name-ar"
                        dir="rtl"
                    >
                        {{ attendee.exhibit.name.ar }}
                    </div>

                </div>

                <!-- Divider -->
                <div class="section-divider">
                    <span></span>
                    <i class="fa-solid fa-star"></i>
                    <span></span>
                </div>

                <!-- Attendee Information -->
                <div class="attendee-section">

                    <div class="attendee-label">
                        {{ $i18n.locale === 'ar'
                            ? 'المشارك'
                            : 'ATTENDEE' }}
                    </div>

                    <h1 class="attendee-name">
                        {{ attendee.name }}
                    </h1>

                    <!-- Phone -->
                    <a
                        v-if="attendee.phone"
                        :href="`tel:${attendee.phone}`"
                        class="phone-button"
                    >
                        <span class="phone-icon">
                            <i class="fa-solid fa-phone"></i>
                        </span>

                        <span class="phone-content">
                            <small>
                                {{ $i18n.locale === 'ar'
                                    ? 'اتصل الآن'
                                    : 'Call now' }}
                            </small>

                            <strong dir="ltr">
                                {{ attendee.phone }}
                            </strong>
                        </span>
                    </a>

                </div>

                <!-- Footer -->
                <div class="card-footer">

                    <div class="verified">
                        <i class="fa-solid fa-circle-check"></i>

                        <span>
                            {{ $i18n.locale === 'ar'
                                ? 'بيانات مشارك موثقة'
                                : 'Verified Attendee' }}
                        </span>
                    </div>

                </div>

            </div>

        </main>

        <FooterTop :bg="'theme-bg'" />
        <FooterDark />
        <ScrollToTop />
    </div>
</template>

<script setup>
import FooterDark from '@/components/footer/footer-dark.vue'
import FooterTop from '@/components/footer/footer-top.vue'
import NavbarDark from '@/components/navbar/navbar-dark.vue'
import ScrollToTop from '@/components/scroll-to-top.vue'

import { ref, computed, onMounted, defineProps } from 'vue'
import axios from 'axios'

const props = defineProps({
    code: {
        type: String,
        required: true
    }
})

const attendee = ref(null)
const loading = ref(true)
const error = ref(null)

const exhibitName = computed(() => {
    return attendee.value?.exhibit?.name?.en
        || attendee.value?.exhibit?.name?.ar
        || 'Exhibit'
})

const fetchAttendee = async () => {
    try {
        loading.value = true
        error.value = null

        const response = await axios.get(
            `/attendees/${props.code}`
        )

        attendee.value = response.data.data

    } catch (err) {
        console.error(err)

        if (err.response?.status === 404) {
            error.value = 'Attendee not found.'
        } else {
            error.value = 'Something went wrong.'
        }
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    fetchAttendee()
})
</script>

<style scoped>
.attendee-wrapper {
    min-height: 100vh;
    background: #f4f6f8;
}

.attendee-page {
    position: relative;
    min-height: 680px;

    display: flex;
    align-items: center;
    justify-content: center;

    padding: 70px 20px;

    overflow: hidden;
}

.background-decoration {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
}

.decoration-one {
    width: 420px;
    height: 420px;

    top: -220px;
    right: -150px;

    border: 1px solid rgba(30, 45, 60, 0.08);
}

.decoration-two {
    width: 330px;
    height: 330px;

    bottom: -180px;
    left: -130px;

    border: 1px solid rgba(30, 45, 60, 0.07);
}

.attendee-card {
    position: relative;
    z-index: 2;

    width: 100%;
    max-width: 500px;

    overflow: hidden;

    background: rgba(255, 255, 255, 0.96);

    border: 1px solid rgba(20, 30, 40, 0.07);

    border-radius: 28px;

    padding: 0 30px 28px;

    text-align: center;

    box-shadow:
        0 30px 70px rgba(25, 35, 45, 0.12),
        0 8px 25px rgba(25, 35, 45, 0.05);

    animation: card-enter 0.6s ease both;
}

@keyframes card-enter {
    from {
        opacity: 0;
        transform: translateY(25px) scale(0.98);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.banner-section {
    width: calc(100% + 60px);

    margin-left: -30px;
    margin-right: -30px;

    height: 180px;

    overflow: hidden;

    background: #f1f3f4;
}

.exhibit-banner {
    width: 100%;
    height: 100%;

    display: block;

    object-fit: cover;
    object-position: center;

    transition: transform 0.4s ease;
}

.logo-section {
    padding-top: 35px;
}

.logo-placeholder {
    width: 150px;
    height: 110px;

    margin: 0 auto;

    display: flex;
    align-items: center;
    justify-content: center;

    background: #ffffff;

    border: 1px solid rgba(20, 30, 40, 0.07);

    border-radius: 20px;

    box-shadow:
        0 12px 30px rgba(20, 30, 40, 0.08);

    font-size: 34px;

    color: #9aa4ab;
}


.exhibit-section {
    margin-top: 25px;
}

.exhibit-name-en {
    font-size: 20px;
    line-height: 1.45;

    font-weight: 700;

    color: #202c35;
}

.exhibit-name-ar {
    margin-top: 7px;

    font-size: 19px;
    line-height: 1.6;

    font-weight: 500;

    color: #68747d;
}


.section-divider {
    display: flex;
    align-items: center;
    justify-content: center;

    gap: 12px;

    margin: 28px 0 24px;
}

.section-divider span {
    width: 55px;
    height: 1px;

    background: #e3e7ea;
}

.section-divider i {
    font-size: 9px;
    color: #9aa4ab;
}


.attendee-section {
    padding: 0 5px;
}

.attendee-label {
    display: inline-flex;
    align-items: center;

    padding: 6px 13px;

    border-radius: 30px;

    background: #f0f3f5;

    color: #71808a;

    font-size: 11px;

    font-weight: 700;

    letter-spacing: 1.5px;
}

.attendee-name {
    margin: 14px 0 23px;

    color: #17232c;

    font-size: 30px;

    line-height: 1.25;

    font-weight: 700;

    word-break: break-word;
}


.phone-button {
    position: relative;

    display: flex;
    align-items: center;

    width: 100%;

    padding: 12px 14px;

    border-radius: 17px;

    background: #172630;

    color: #ffffff;

    text-decoration: none;

    text-align: start;

    transition:
        transform 0.25s ease,
        box-shadow 0.25s ease,
        background 0.25s ease;
}

.phone-button:hover {
    transform: translateY(-2px);

    background: #21343f;

    color: #ffffff;

    box-shadow:
        0 12px 25px rgba(20, 35, 45, 0.18);
}

.phone-icon {
    width: 45px;
    height: 45px;

    flex: 0 0 45px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 13px;

    background: rgba(255, 255, 255, 0.1);

    font-size: 16px;
}

.phone-content {
    display: flex;
    flex-direction: column;

    gap: 2px;

    margin-inline-start: 13px;

    overflow: hidden;
}

.phone-content small {
    font-size: 10px;

    opacity: 0.6;

    text-transform: uppercase;

    letter-spacing: 1px;
}

.phone-content strong {
    font-size: 16px;

    font-weight: 600;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.phone-arrow {
    margin-inline-start: auto;

    opacity: 0.6;

    font-size: 15px;
}


.card-footer {
    margin-top: 25px;
}

.verified {
    display: inline-flex;
    align-items: center;

    gap: 7px;

    color: #89949c;

    font-size: 12px;
}

.verified i {
    color: #647681;
    font-size: 15px;
}


.state-container {
    position: relative;
    z-index: 2;

    width: 100%;

    display: flex;
    justify-content: center;
}

.loading-card {
    width: 100%;
    max-width: 430px;

    padding: 45px 30px;

    background: #ffffff;

    border-radius: 25px;

    text-align: center;

    box-shadow: 0 25px 60px rgba(20, 30, 40, 0.09);
}

.loading-logo {
    width: 90px;
    height: 70px;

    margin: 0 auto 30px;

    border-radius: 16px;

    background: #edf0f2;

    display: flex;
    align-items: center;
    justify-content: center;
}

.loading-logo span {
    width: 28px;
    height: 28px;

    border: 3px solid #cbd2d7;
    border-top-color: #536571;

    border-radius: 50%;

    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.loading-line {
    height: 11px;

    width: 70%;

    margin: 12px auto;

    border-radius: 10px;

    background: #edf0f2;
}

.loading-line-large {
    width: 85%;
}

.loading-line-small {
    width: 50%;
}

.loading-card p {
    margin-top: 25px;

    color: #89949c;

    font-size: 14px;
}

.error-card {
    width: 100%;
    max-width: 430px;

    padding: 45px 30px;

    text-align: center;

    background: #ffffff;

    border-radius: 25px;

    box-shadow: 0 25px 60px rgba(20, 30, 40, 0.09);
}

.error-icon {
    width: 75px;
    height: 75px;

    margin: 0 auto 20px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 50%;

    background: #f3f4f5;

    color: #687780;

    font-size: 28px;
}

.error-card h2 {
    margin-bottom: 10px;

    color: #202c35;

    font-size: 22px;
}

.error-card p {
    margin: 0;

    color: #8a959c;

    font-size: 14px;

    line-height: 1.7;
}


@media (max-width: 575px) {

    .attendee-page {
        min-height: 600px;

        padding: 40px 15px;
    }

    .attendee-card {
        max-width: 100%;

        border-radius: 24px;

        padding: 0 20px 23px;
    }

    .logo-section {
        padding-top: 28px;
    }

    .logo-container {
        width: 130px;
        height: 95px;
    }

    .exhibit-name-en {
        font-size: 18px;
    }

    .exhibit-name-ar {
        font-size: 17px;
    }

    .section-divider {
        margin: 23px 0 21px;
    }

    .attendee-name {
        font-size: 26px;
    }

    .phone-content strong {
        font-size: 15px;
    }

    .background-decoration {
        opacity: 0.5;
    }
}


@media (max-width: 360px) {

    .attendee-page {
        padding: 30px 10px;
    }

    .attendee-card {
        padding-inline: 16px;
    }

    .attendee-name {
        font-size: 23px;
    }

    .phone-content strong {
        font-size: 14px;
    }
}
</style>